import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  DollarSign, TrendingUp, Upload, Plus, Trash2, Edit2, Check, X,
  IndianRupee, Calculator, BarChart3, FileSpreadsheet
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { API } from "../App";
import { toast } from "sonner";

const fmt = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString("en-IN");
};

const fmtFull = (n) => {
  if (n === null || n === undefined) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
};

const CostTrackingTab = ({ campaignNames = [] }) => {
  const [analytics, setAnalytics] = useState(null);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [form, setForm] = useState({ campaign_name: "", month: "", spend: "", avg_booking_value: "", notes: "" });
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, costsRes] = await Promise.all([
        axios.get(`${API}/campaign-costs/analytics`),
        axios.get(`${API}/campaign-costs`),
      ]);
      setAnalytics(analyticsRes.data);
      setCosts(costsRes.data);
    } catch {
      toast.error("Failed to load cost data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!form.campaign_name || !form.month || !form.spend) {
      toast.error("Campaign, month, and spend are required");
      return;
    }
    try {
      const payload = {
        campaign_name: form.campaign_name,
        month: form.month,
        spend: parseFloat(form.spend),
        avg_booking_value: form.avg_booking_value ? parseFloat(form.avg_booking_value) : null,
        notes: form.notes || null,
      };
      await axios.post(`${API}/campaign-costs`, payload);
      toast.success("Cost entry added");
      setShowAddDialog(false);
      setForm({ campaign_name: "", month: "", spend: "", avg_booking_value: "", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add cost");
    }
  };

  const handleDelete = async (costId) => {
    if (!window.confirm("Delete this cost entry?")) return;
    try {
      await axios.delete(`${API}/campaign-costs/${costId}`);
      toast.success("Deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const startEdit = (cost) => {
    setEditingId(cost.cost_id);
    setEditValues({ spend: cost.spend, avg_booking_value: cost.avg_booking_value || "", notes: cost.notes || "" });
  };

  const saveEdit = async (costId) => {
    try {
      await axios.put(`${API}/campaign-costs/${costId}`, {
        spend: parseFloat(editValues.spend),
        avg_booking_value: editValues.avg_booking_value ? parseFloat(editValues.avg_booking_value) : null,
        notes: editValues.notes || null,
      });
      toast.success("Updated");
      setEditingId(null);
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/campaign-costs/bulk-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded: ${res.data.inserted} inserted, ${res.data.updated} updated`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
      setShowUploadDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Generate month options (last 12 months + next 3)
  const monthOptions = [];
  const now = new Date();
  for (let i = -12; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
    monthOptions.push({ value: val, label });
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-200 rounded-lg" />)}</div>
        <div className="h-64 bg-slate-200 rounded-lg" />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const campAnalytics = analytics?.campaigns || [];
  const monthly = analytics?.monthly || [];

  return (
    <div className="space-y-6" data-testid="cost-tracking-tab">
      {/* Actions Bar */}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => setShowAddDialog(true)} data-testid="add-cost-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Cost Entry
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowUploadDialog(true)} data-testid="upload-costs-btn">
          <Upload className="w-4 h-4 mr-1.5" /> Bulk Upload
        </Button>
      </div>

      {/* Summary Cards */}
      {summary.campaigns_with_costs > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            <Card>
              <CardContent className="p-3 text-center">
                <IndianRupee className="w-4 h-4 mx-auto mb-1 text-amber-500" />
                <p className="text-xl font-bold text-slate-900" data-testid="cost-total-spend">{fmt(summary.total_spend)}</p>
                <p className="text-[10px] text-slate-500">Total Spend</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <Calculator className="w-4 h-4 mx-auto mb-1 text-blue-500" />
                <p className="text-xl font-bold text-blue-600" data-testid="cost-cpl">{summary.overall_cpl ? fmtFull(summary.overall_cpl) : "—"}</p>
                <p className="text-[10px] text-slate-500">CPL</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <DollarSign className="w-4 h-4 mx-auto mb-1 text-violet-500" />
                <p className="text-xl font-bold text-violet-600" data-testid="cost-cpb">{summary.overall_cpb ? fmtFull(summary.overall_cpb) : "—"}</p>
                <p className="text-[10px] text-slate-500">CPB</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <BarChart3 className="w-4 h-4 mx-auto mb-1 text-slate-500" />
                <p className="text-xl font-bold text-slate-900">{summary.total_leads}</p>
                <p className="text-[10px] text-slate-500">Leads</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <BarChart3 className="w-4 h-4 mx-auto mb-1 text-emerald-500" />
                <p className="text-xl font-bold text-emerald-600">{summary.total_booked}</p>
                <p className="text-[10px] text-slate-500">Booked</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <IndianRupee className="w-4 h-4 mx-auto mb-1 text-emerald-500" />
                <p className="text-xl font-bold text-emerald-600">{summary.estimated_revenue ? fmt(summary.estimated_revenue) : "—"}</p>
                <p className="text-[10px] text-slate-500">Est. Revenue</p>
              </CardContent>
            </Card>
            <Card className={summary.overall_roi_pct !== null ? (summary.overall_roi_pct >= 0 ? "border-emerald-200 bg-emerald-50/30" : "border-red-200 bg-red-50/30") : ""}>
              <CardContent className="p-3 text-center">
                <TrendingUp className="w-4 h-4 mx-auto mb-1 text-amber-500" />
                <p className={`text-xl font-bold ${summary.overall_roi_pct >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="cost-roi">
                  {summary.overall_roi_pct !== null ? `${summary.overall_roi_pct}%` : "—"}
                </p>
                <p className="text-[10px] text-slate-500">ROI</p>
              </CardContent>
            </Card>
          </div>

          {/* Per-Campaign Table */}
          <Card>
            <CardHeader><CardTitle className="text-base">Campaign Cost Performance</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Total Spend</TableHead>
                    <TableHead className="text-right">Leads</TableHead>
                    <TableHead className="text-right">Booked</TableHead>
                    <TableHead className="text-right">CPL</TableHead>
                    <TableHead className="text-right">CPB</TableHead>
                    <TableHead className="text-right">Avg Booking Val</TableHead>
                    <TableHead className="text-right">ROI</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campAnalytics.map((c) => (
                    <TableRow key={c.campaign_name} data-testid={`cost-camp-${c.campaign_name}`}>
                      <TableCell className="font-medium text-slate-900">{c.campaign_name}</TableCell>
                      <TableCell className="text-right font-mono">{fmtFull(c.total_spend)}</TableCell>
                      <TableCell className="text-right font-mono">{c.total_leads}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{c.total_booked}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{c.cpl ? fmtFull(c.cpl) : "—"}</TableCell>
                      <TableCell className="text-right font-mono text-violet-600">{c.cpb ? fmtFull(c.cpb) : "—"}</TableCell>
                      <TableCell className="text-right font-mono">{c.avg_booking_value ? fmtFull(c.avg_booking_value) : <span className="text-slate-400 text-xs">Not set</span>}</TableCell>
                      <TableCell className="text-right">
                        {c.roi_pct !== null ? (
                          <Badge variant="outline" className={`font-mono text-xs ${c.roi_pct >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                            {c.roi_pct}%
                          </Badge>
                        ) : <span className="text-slate-400 text-xs">Need booking val</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Monthly Breakdown */}
          {monthly.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Monthly Spend Breakdown</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Spend</TableHead>
                      <TableHead className="text-right">Campaigns</TableHead>
                      <TableHead className="text-right">Leads</TableHead>
                      <TableHead className="text-right">Booked</TableHead>
                      <TableHead className="text-right">CPL</TableHead>
                      <TableHead className="text-right">CPB</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthly.map((m) => (
                      <TableRow key={m.month} data-testid={`cost-month-${m.month}`}>
                        <TableCell className="font-medium">{new Date(m.month + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</TableCell>
                        <TableCell className="text-right font-mono">{fmtFull(m.spend)}</TableCell>
                        <TableCell className="text-right font-mono">{m.campaigns}</TableCell>
                        <TableCell className="text-right font-mono">{m.leads}</TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">{m.booked}</TableCell>
                        <TableCell className="text-right font-mono text-blue-600">{m.cpl ? fmtFull(m.cpl) : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-violet-600">{m.cpb ? fmtFull(m.cpb) : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center text-slate-400">
            <IndianRupee className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-base font-medium mb-1">No cost data yet</p>
            <p className="text-sm">Add campaign spend data manually or upload via Excel to see CPL, CPB, and ROI metrics.</p>
          </CardContent>
        </Card>
      )}

      {/* Cost Entries Table */}
      {costs.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">All Cost Entries</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Avg Booking Value</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costs.map((c) => (
                  <TableRow key={c.cost_id} data-testid={`cost-entry-${c.cost_id}`}>
                    <TableCell className="font-medium">{c.campaign_name}</TableCell>
                    <TableCell>{new Date(c.month + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</TableCell>
                    <TableCell className="text-right">
                      {editingId === c.cost_id ? (
                        <Input type="number" className="w-28 h-7 text-xs text-right" value={editValues.spend} onChange={e => setEditValues(v => ({ ...v, spend: e.target.value }))} />
                      ) : <span className="font-mono">{fmtFull(c.spend)}</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {editingId === c.cost_id ? (
                        <Input type="number" className="w-32 h-7 text-xs text-right" placeholder="Optional" value={editValues.avg_booking_value} onChange={e => setEditValues(v => ({ ...v, avg_booking_value: e.target.value }))} />
                      ) : <span className="font-mono">{c.avg_booking_value ? fmtFull(c.avg_booking_value) : <span className="text-slate-400">—</span>}</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[120px] truncate">{c.notes || "—"}</TableCell>
                    <TableCell>
                      {editingId === c.cost_id ? (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveEdit(c.cost_id)} data-testid={`save-edit-${c.cost_id}`}><Check className="w-3.5 h-3.5 text-emerald-600" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}><X className="w-3.5 h-3.5 text-slate-400" /></Button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(c)} data-testid={`edit-cost-${c.cost_id}`}><Edit2 className="w-3.5 h-3.5 text-slate-500" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(c.cost_id)} data-testid={`delete-cost-${c.cost_id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add Cost Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md" data-testid="add-cost-dialog">
          <DialogHeader>
            <DialogTitle>Add Campaign Cost</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Campaign</Label>
              <Select value={form.campaign_name} onValueChange={v => setForm(f => ({ ...f, campaign_name: v }))}>
                <SelectTrigger data-testid="cost-campaign-select"><SelectValue placeholder="Select campaign" /></SelectTrigger>
                <SelectContent>
                  {campaignNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Month</Label>
              <Select value={form.month} onValueChange={v => setForm(f => ({ ...f, month: v }))}>
                <SelectTrigger data-testid="cost-month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
                <SelectContent>
                  {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Spend Amount (INR)</Label>
              <Input type="number" placeholder="e.g. 150000" value={form.spend} onChange={e => setForm(f => ({ ...f, spend: e.target.value }))} data-testid="cost-spend-input" />
            </div>
            <div>
              <Label className="text-xs">Avg Booking Value (optional, for ROI calc)</Label>
              <Input type="number" placeholder="e.g. 3500000" value={form.avg_booking_value} onChange={e => setForm(f => ({ ...f, avg_booking_value: e.target.value }))} data-testid="cost-abv-input" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input placeholder="e.g. Facebook Lead Gen" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="cost-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAdd} data-testid="save-cost-btn">Save Cost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md" data-testid="upload-cost-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Campaign Costs</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 space-y-2">
              <p className="font-medium text-slate-800 flex items-center gap-1.5">
                <FileSpreadsheet className="w-4 h-4" /> Excel Format Required
              </p>
              <p>Your file must have these columns:</p>
              <ul className="list-disc list-inside text-xs space-y-1">
                <li><span className="font-mono font-medium">Campaign Name</span> — must match existing campaign names</li>
                <li><span className="font-mono font-medium">Month</span> — format: YYYY-MM (e.g., 2025-10)</li>
                <li><span className="font-mono font-medium">Spend</span> — amount in INR</li>
                <li><span className="font-mono font-medium">Avg Booking Value</span> — optional, for ROI</li>
                <li><span className="font-mono font-medium">Notes</span> — optional</li>
              </ul>
              <p className="text-xs text-slate-500">Duplicate campaign+month entries will be updated (upsert).</p>
            </div>
            <div>
              <Label className="text-xs">Select Excel File</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={handleUpload} disabled={uploading} data-testid="cost-file-input" />
              {uploading && <p className="text-xs text-blue-500 mt-1">Uploading...</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CostTrackingTab;
